import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";

export default function EmailLogManager() {
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: logs = [], refetch, isFetching } = useQuery({
    queryKey: ["emailLog", statusFilter],
    queryFn: async () => {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      return (await api.get(`/admin/email-log${q}`)).data;
    },
    refetchInterval: 30_000,
  });

  const resendEmail = async (entry) => {
    if (!window.confirm(`Resend this email to ${entry.to_primary}?`)) return;
    try {
      const { data } = await api.post(`/admin/email-log/${entry.id}/resend`, {});
      toast.success(`Resent to ${data.to}`);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const exportCsv = () => {
    const headers = ["status", "to", "subject", "sent_at", "resend_msg_id", "error"];
    const rows = logs.map((e) => [
      e.status, e.to_primary, e.subject, e.sent_at, e.resend_msg_id || "", e.error || "",
    ]);
    downloadCsv("email-log", headers, rows);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1 bg-gg-bg border border-gg-border rounded-full p-1 w-fit">
          {["all", "sent", "failed", "skipped"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors capitalize ${
                statusFilter === s ? "bg-gg-green text-white" : "text-gg-brown hover:text-[var(--gg-text)]"
              }`}
              data-testid={`emaillog-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-xs px-3 py-1.5 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1"
          data-testid="emaillog-refresh"
        >
          <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          type="button"
          onClick={exportCsv}
          disabled={logs.length === 0}
          className="text-xs px-3 py-1.5 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="emaillog-export-csv"
        >
          Export CSV
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
          {statusFilter === "all" ? "No emails sent yet." : `No ${statusFilter} emails.`}
        </div>
      ) : (
        <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
              <tr>
                <th className="px-4 py-2 text-left w-20">Status</th>
                <th className="px-4 py-2 text-left">Recipient</th>
                <th className="px-4 py-2 text-left">Subject</th>
                <th className="px-4 py-2 text-left">Sent</th>
                <th className="px-4 py-2 text-left">Resend msg id</th>
                <th className="px-4 py-2 text-left w-20">Action</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((e) => (
                <tr key={e.id} className="border-t border-gg-border" data-testid={`emaillog-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs">{e.to_primary}</div>
                    {e.has_attachment && (
                      <div className="text-[10px] uppercase tracking-[0.14em] text-gg-brown mt-0.5">📎 has attachment</div>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="truncate">{e.subject}</div>
                    {e.error && (
                      <div className="text-[11px] text-[#B24C4C] mt-1">⚠ {e.error}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gg-muted whitespace-nowrap">{formatDate(e.sent_at)}</td>
                  <td className="px-4 py-3 text-xs font-mono text-gg-muted">
                    {e.resend_msg_id ? (
                      <a
                        href={`https://resend.com/emails/${e.resend_msg_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-gg-green"
                        title="Open in Resend dashboard"
                      >
                        {e.resend_msg_id.slice(0, 8)}…
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {e.status === "sent" && (
                      <button
                        type="button"
                        onClick={() => resendEmail(e)}
                        className="text-xs px-2.5 py-1 rounded-full border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1"
                        title="Resend this email"
                        data-testid={`emaillog-resend-${e.id}`}
                      >
                        <Send size={11} /> Resend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2 text-[11px] text-gg-muted italic border-t border-gg-border">
            Showing latest {logs.length} of last 50. Click a Resend msg id to open delivery details in the Resend dashboard.
          </p>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "sent") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gg-green/15 text-gg-green font-semibold">
        <CheckCircle2 size={12} /> Sent
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#B24C4C]/15 text-[#B24C4C] font-semibold">
        <XCircle size={12} /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#D9933B]/15 text-[#9b6a26] font-semibold">
      <AlertTriangle size={12} /> Skipped
    </span>
  );
}
